/**
 * Top-level /recordings — search, play, download.
 *
 * Gated by `recordings_browser` module permission. Client users only see
 * recordings whose agent belongs to their tenant (enforced server-side).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { recordingsApi, screenRecordingsApi, qaApi } from '../../api/client';
import HelpButton from '../help/HelpButton';
import { useAuth } from '../../auth/AuthProvider';
import EvaluationViewerModal from './qa/EvaluationViewerModal';

// Roles that can create/edit QA evaluations from this page.
const QA_EVAL_WRITER_ROLES = new Set(['admin', 'supervisor', 'qa', 'qa_manager']);
// Roles with QA-manager-level powers (delete, reassign, edit any).
const QA_MANAGER_ROLES = new Set(['admin', 'qa_manager']);

const PAGE_SIZE = 25;

// Timezone choices for the Recordings view. Default = America/Chicago (Central).
// 'system' uses the browser's local zone; 'utc' is convenient for auditing.
const TZ_CHOICES = [
  { v: 'America/Chicago',      l: 'Central (CT)' },
  { v: 'America/New_York',     l: 'Eastern (ET)' },
  { v: 'America/Denver',       l: 'Mountain (MT)' },
  { v: 'America/Phoenix',      l: 'Arizona (MST, no DST)' },
  { v: 'America/Los_Angeles',  l: 'Pacific (PT)' },
  { v: 'America/Anchorage',    l: 'Alaska (AKT)' },
  { v: 'Pacific/Honolulu',     l: 'Hawaii (HST)' },
  { v: 'America/Mexico_City',  l: 'Mexico City (CST/CDT)' },
  { v: 'America/Monterrey',    l: 'Monterrey (CST/CDT)' },
  { v: 'utc',                  l: 'UTC' },
  { v: 'system',               l: 'Browser local' },
];
const TZ_STORAGE_KEY = 'cx.recordings.tz';
const DEFAULT_TZ = 'America/Chicago';

function loadTz() {
  try {
    const v = localStorage.getItem(TZ_STORAGE_KEY);
    if (v && TZ_CHOICES.some((t) => t.v === v)) return v;
  } catch { /* ignore */ }
  return DEFAULT_TZ;
}
function saveTz(v) {
  try { localStorage.setItem(TZ_STORAGE_KEY, v); } catch { /* ignore */ }
}

function fmtInTz(iso, tz) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (tz === 'system') return d.toLocaleString();
  const options = {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
    timeZone: tz === 'utc' ? 'UTC' : tz,
    timeZoneName: 'short',
  };
  try {
    return new Intl.DateTimeFormat(undefined, options).format(d);
  } catch {
    return d.toLocaleString();
  }
}

const DIRECTIONS = [
  { v: '', l: '— any —' },
  { v: 'inbound', l: 'Inbound' },
  { v: 'outbound', l: 'Outbound' },
  { v: 'internal', l: 'Internal' },
];
const TIERS = [
  { v: '', l: 'Active (local + archived)' },
  { v: 'local', l: 'Local only' },
  { v: 'archived', l: 'Archived only' },
];

function fmtDuration(s) {
  if (!s || s < 1) return '—';
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
}
function fmtBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function RecordingsBrowserPage() {
  const [filters, setFilters] = useState({
    started_from: '',
    started_to: '',
    agent_extension: '',
    agent_name: '',
    queue_id: '',
    skill_level: '',
    direction: '',
    did: '',
    caller_id: '',
    min_duration_sec: '',
    storage_tier: '',
  });
  const [page, setPage] = useState(1);
  const [playing, setPlaying] = useState(null);
  const [hasScreenSet, setHasScreenSet] = useState(new Set());

  // Popout helper.
  const openPopout = (rec) => {
    if (!rec?.id) return;
    const url = `/playback/${encodeURIComponent(rec.id)}`;
    const name = `cx-playback-${rec.id}`;
    const features = 'width=1400,height=900,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes';
    window.open(url, name, features);
  };
  const [tz, setTz] = useState(loadTz());
  const onTzChange = (v) => { setTz(v); saveTz(v); };

  // Tenant-scoped filter options — these come from recordings the current user
  // is allowed to see. Continuum employees get all; client users get theirs.
  const { data: filterOpts } = useQuery({
    queryKey: ['recordings', 'my-filters'],
    queryFn: () => recordingsApi.myFilters(),
    staleTime: 60_000,
  });
  const queues = filterOpts?.queues || [];
  const agentOptions = filterOpts?.agents || [];
  const skillOptions = filterOpts?.skill_levels || [];
  const didOptions = filterOpts?.dids || [];

  const queryParams = useMemo(() => {
    const out = { page, page_size: PAGE_SIZE };
    for (const [k, v] of Object.entries(filters)) {
      if (v) out[k] = k.startsWith('started_') ? new Date(v).toISOString() : v;
    }
    return out;
  }, [filters, page]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['recordings', queryParams],
    queryFn: () => recordingsApi.list(queryParams),
    keepPreviousData: true,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // 2026-07-28: unrecorded (bypassed) sessions — agents who proceeded WITHOUT
  // screen recording because Azure connectivity failed and their tenant
  // allows bypass. Surfaced here so QA/compliance can see who worked
  // unrecorded and why.
  const [showBypasses, setShowBypasses] = useState(false);
  const { data: bypassData } = useQuery({
    queryKey: ['screen-bypasses', 7],
    queryFn: () => screenRecordingsApi.listBypasses({ days: 7 }),
    staleTime: 60_000,
  });
  const bypasses = bypassData?.items || [];

  // After each list load, bulk-query which call_ids have a linked screen
  // recording so the row can render a 🎥 Popout button.
  useEffect(() => {
    const callIds = items.map(r => r.call_id).filter(Boolean);
    if (callIds.length === 0) {
      setHasScreenSet(new Set());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await screenRecordingsApi.hasScreen(callIds);
        if (!cancelled) setHasScreenSet(new Set(resp?.with_screen || []));
      } catch (err) {
        // Non-fatal — fall back to no popout buttons.
        console.warn('[recordings] has-screen lookup failed', err);
        if (!cancelled) setHasScreenSet(new Set());
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const set = (k, v) => { setFilters({ ...filters, [k]: v }); setPage(1); };
  const clearAll = () => {
    setFilters({
      started_from: '', started_to: '', agent_extension: '', agent_name: '',
      queue_id: '', skill_level: '', direction: '', did: '', caller_id: '',
      min_duration_sec: '', storage_tier: '',
    });
    setPage(1);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {bypasses.length > 0 && (
        <div className="mb-4 border border-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-thin">
          <button
            type="button"
            onClick={() => setShowBypasses((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 text-left"
          >
            <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              ⚠️ {bypasses.length} unrecorded session{bypasses.length === 1 ? '' : 's'} in the last 7 days
              <span className="font-normal"> — agents proceeded without screen recording (connectivity blocked)</span>
            </span>
            <span className="text-amber-700 dark:text-amber-300 text-xs">{showBypasses ? 'Hide ▲' : 'Show ▼'}</span>
          </button>
          {showBypasses && (
            <div className="px-3 pb-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-surface-muted dark:text-surface-dark-muted">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium">When</th>
                    <th className="px-2 py-1 text-left font-medium">Agent</th>
                    <th className="px-2 py-1 text-left font-medium">Ext</th>
                    <th className="px-2 py-1 text-left font-medium">Cluster</th>
                    <th className="px-2 py-1 text-left font-medium">IP</th>
                    <th className="px-2 py-1 text-left font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {bypasses.map((b) => (
                    <tr key={b.id} className="border-t border-amber-200 dark:border-amber-800">
                      <td className="px-2 py-1 whitespace-nowrap">{new Date(b.occurred_at).toLocaleString()}</td>
                      <td className="px-2 py-1">{b.agent_name || '—'}</td>
                      <td className="px-2 py-1">{b.agent_extension || '—'}</td>
                      <td className="px-2 py-1">{b.cluster || '—'}</td>
                      <td className="px-2 py-1">{b.ip || '—'}</td>
                      <td className="px-2 py-1 font-mono text-amber-700 dark:text-amber-400 break-all">{b.preflight_error || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h1 className="text-lg font-semibold">Recordings</h1>
          <p className="text-sm text-surface-muted dark:text-surface-dark-muted">
            Search call recordings across active (local) and archived storage.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton moduleKey="recordings_browser" />
          <label className="text-xs text-surface-muted dark:text-surface-dark-muted">Time zone</label>
          <select
            value={tz}
            onChange={(e) => onTzChange(e.target.value)}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin text-sm"
          >
            {TZ_CHOICES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <button onClick={() => refetch()} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">↻ Refresh</button>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-surface-border dark:border-surface-dark-border rounded-thin p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Field label="From"><input type="datetime-local" value={filters.started_from} onChange={(e) => set('started_from', e.target.value)} className="input" /></Field>
          <Field label="To"><input type="datetime-local" value={filters.started_to} onChange={(e) => set('started_to', e.target.value)} className="input" /></Field>
          <Field label="Agent">
            <select value={filters.agent_extension} onChange={(e) => set('agent_extension', e.target.value)} className="input">
              <option value="">— any —</option>
              {agentOptions.map((a) => (
                <option key={a.extension} value={a.extension}>{a.extension} · {a.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Agent name (contains)"><input value={filters.agent_name} onChange={(e) => set('agent_name', e.target.value)} className="input" placeholder="part of name" /></Field>
          <Field label="Queue">
            <select value={filters.queue_id} onChange={(e) => set('queue_id', e.target.value)} className="input">
              <option value="">— any —</option>
              {queues.map((q) => <option key={q.queue_id} value={q.queue_id}>{q.queue_id} · {q.name}</option>)}
            </select>
          </Field>
          <Field label="Skill level">
            <select value={filters.skill_level} onChange={(e) => set('skill_level', e.target.value)} className="input">
              <option value="">— any —</option>
              {(skillOptions.length ? skillOptions : [1,2,3,4,5]).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          <Field label="Direction">
            <select value={filters.direction} onChange={(e) => set('direction', e.target.value)} className="input">
              {DIRECTIONS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
            </select>
          </Field>
          <Field label="DID / DN">
            {didOptions.length > 0 ? (
              <select value={filters.did} onChange={(e) => set('did', e.target.value)} className="input">
                <option value="">— any —</option>
                {didOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            ) : (
              <input value={filters.did} onChange={(e) => set('did', e.target.value)} className="input" placeholder="e.g. +19294510060" />
            )}
          </Field>
          <Field label="Caller"><input value={filters.caller_id} onChange={(e) => set('caller_id', e.target.value)} className="input" /></Field>
          <Field label="Min duration (sec)"><input type="number" value={filters.min_duration_sec} onChange={(e) => set('min_duration_sec', e.target.value)} className="input" /></Field>
          <Field label="Storage tier">
            <select value={filters.storage_tier} onChange={(e) => set('storage_tier', e.target.value)} className="input">
              {TIERS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </Field>
          <Field label=" ">
            <button onClick={clearAll} className="w-full px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">Clear filters</button>
          </Field>
        </div>
        <style>{`.input { width: 100%; padding: 4px 8px; background: transparent; border: 1px solid currentColor; border-color: rgb(209 213 219 / 0.5); border-radius: 4px; font-size: 14px; }
        .dark .input { border-color: rgb(75 85 99 / 0.5); }`}</style>
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="text-sm text-surface-muted">Loading…</div>
      ) : error ? (
        <div className="text-sm text-red-600">{String(error.detail || error.message)}</div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2 text-sm">
            <div className="text-surface-muted">{total.toLocaleString()} result{total === 1 ? '' : 's'}</div>
            <Pagination page={page} totalPages={totalPages} onPage={setPage} />
          </div>

          {/* 2026-07-16: wrap in overflow-x-auto so on narrower screens the
              table gets a horizontal scrollbar instead of cells overlapping.
              Also apply min-w-max so browser doesn't try to force-fit. */}
          <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-x-auto">
            <table className="w-full text-sm min-w-[1200px]">
              <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
                <tr>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Started</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Agent</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Queue</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">From</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">To</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Dir</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Duration</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Size</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Tier</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Evaluated</th>
                  <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={11} className="px-3 py-6 text-center text-surface-muted">No recordings match these filters.</td></tr>
                )}
                {items.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr className="border-t border-surface-border dark:border-surface-dark-border">
                      <td className="px-3 py-2 whitespace-nowrap" title={`UTC: ${r.started_at}`}>{fmtInTz(r.started_at, tz)}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{r.agent_name || '—'}</div>
                        <div className="text-xs text-surface-muted">{r.agent_extension || ''}</div>
                      </td>
                      <td className="px-3 py-2">{r.queue_name ? `${r.queue_id} · ${r.queue_name}` : (r.queue_id || '—')}</td>
                      {/* 2026-07-16: From/To split by direction.
                          - outbound: From=agent_extension, To=caller_id (destination dialed)
                          - inbound:  From=caller_id (external), To=did OR agent_extension */}
                      <td className="px-3 py-2 font-mono text-xs">
                        {r.direction === 'outbound'
                          ? (r.agent_extension || '—')
                          : (r.caller_id || '—')}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {r.direction === 'outbound'
                          ? (r.caller_id || r.did || '—')
                          : (r.did || r.agent_extension || '—')}
                      </td>
                      <td className="px-3 py-2 capitalize">{r.direction}</td>
                      <td className="px-3 py-2">{fmtDuration(r.duration_sec)}</td>
                      <td className="px-3 py-2">{fmtBytes(r.file_size)}</td>
                      <td className="px-3 py-2">
                        <span className={
                          'px-2 py-0.5 rounded-thin text-xs ' +
                          (r.storage_tier === 'local'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300')
                        }>{r.storage_tier}</span>
                      </td>
                      <EvaluatedCell r={r} />
                      {/* 2026-07-16: use inline-flex + wrap so the multiple
                          action buttons stack cleanly instead of overflowing.
                          Also whitespace-nowrap on each button keeps them
                          individually intact. */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                          {r.cx_interaction_id && (
                            <button
                              onClick={() => { try { navigator.clipboard.writeText(r.cx_interaction_id); } catch (_) {} }}
                              className="text-surface-muted hover:text-continuum-600 dark:hover:text-continuum-200 text-xs font-mono whitespace-nowrap"
                              title={`Interaction ID: ${r.cx_interaction_id} (click to copy — voice+screen as one unit)`}
                            >🔗 ID</button>
                          )}
                          {hasScreenSet.has(r.call_id) ? (
                            <button
                              onClick={() => openPopout(r)}
                              className="text-continuum-600 dark:text-continuum-200 text-sm whitespace-nowrap"
                              title="Open call playback with screen recording in new window"
                            >🎥 Popout ↗</button>
                          ) : (
                            <button
                              onClick={() => setPlaying(playing?.id === r.id ? null : r)}
                              className="text-continuum-600 dark:text-continuum-200 text-sm whitespace-nowrap"
                            >{playing?.id === r.id ? '× Close' : '▶ Play'}</button>
                          )}
                          <a
                            href={recordingsApi.downloadUrl(r.id)}
                            className="text-continuum-600 dark:text-continuum-200 text-sm whitespace-nowrap"
                            download
                          >↓ Voice</a>
                          {hasScreenSet.has(r.call_id) && (
                            <a href={`/api/recordings/${r.id}/download-combined`}
                               className="text-continuum-600 dark:text-continuum-200 text-sm whitespace-nowrap"
                               download
                               title="Download voice+screen as a single MP4 (may take a while)"
                            >↓ MP4</a>
                          )}
                          <EvaluateAction r={r} />
                        </div>
                      </td>
                    </tr>
                    {playing?.id === r.id && (
                      <tr className="bg-surface-subtle/50 dark:bg-surface-dark-subtle/50">
                        <td colSpan={11} className="px-3 py-3">
                          <DualPlayer recording={r} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-end mt-3">
            <Pagination page={page} totalPages={totalPages} onPage={setPage} />
          </div>
        </>
      )}
    </div>
  );
}


function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border disabled:opacity-30">‹</button>
      <span>Page {page} / {totalPages}</span>
      <button disabled={page >= totalPages} onClick={() => onPage(page + 1)} className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border disabled:opacity-30">›</button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-surface-muted dark:text-surface-dark-muted mb-1">{label}</span>
      {children}
    </label>
  );
}


// -------------------------------------------------------------------------
// DualPlayer (Phase 2, 2026-06-22)
//
// Renders the voice recording AND, if a linked screen recording exists,
// the screen recording side-by-side. Both elements share a single
// scrubber: dragging on the voice element drags the screen one too, with
// the server-computed sync_offset_ms applied.
//
// sync_offset_ms semantics (from backend):
//   positive  -> screen started AFTER voice (the typical case, ~200-500ms)
//   negative  -> screen started BEFORE voice (possible in always-on mode)
//
// We treat voice as the master clock. Screen lags or leads by offset_ms.
// -------------------------------------------------------------------------
function DualPlayer({ recording }) {
  const audioRef  = useRef(null);
  const videoRef  = useRef(null);
  const [screenMeta, setScreenMeta] = useState(null);   // null | { id, sync_offset_ms, status }
  const [lookupDone, setLookupDone] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  // 2026-07-15: pre-buffer gate for the inline player (mirror of the
  // popout DualPlayerPopout logic). Prevents the audio (local, fast) from
  // starting seconds before the screen (Azure remote, slow) has enough
  // buffered data to play smoothly.
  const [ready, setReady] = useState(false);
  const [buffering, setBuffering] = useState(false);

  // Try to find a linked screen recording by call_id. 404 just means "no
  // screen rec exists" — that's the common case for older calls and
  // anyone whose screen toggle is off.
  useEffect(() => {
    let cancelled = false;
    setScreenMeta(null);
    setLookupDone(false);
    setLookupError(null);
    if (!recording?.call_id) {
      setLookupDone(true);
      return;
    }
    (async () => {
      try {
        const meta = await screenRecordingsApi.getByCall(recording.call_id);
        if (!cancelled) {
          setScreenMeta(meta);
          setLookupDone(true);
        }
      } catch (err) {
        if (!cancelled) {
          if (err?.status !== 404) setLookupError(err);
          setLookupDone(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [recording?.id, recording?.call_id]);

  // 2026-07-15 pm: consider a screen recording playable if it has an id
  // AND EITHER status='complete' OR (status in failed/aborted AND file_size>0).
  // Reaper-marked-failed rows are often actually fine - the blob is uploaded,
  // the tab just closed before the /end call.
  const hasScreen = !!(
    screenMeta && screenMeta.id && (
      screenMeta.status === 'complete' ||
      (['failed','aborted'].includes(screenMeta.status) && (screenMeta.file_size || 0) > 0)
    )
  );
  const offsetSec = (screenMeta?.sync_offset_ms ?? 0) / 1000;

  // Pre-buffer readiness gate: wait for BOTH tracks to reach readyState>=3
  // (HAVE_FUTURE_DATA) before allowing play. For voice-only recordings we
  // still wait on the audio track for consistency.
  useEffect(() => {
    // Reset when the recording changes.
    setReady(false);
    const a = audioRef.current;
    const v = hasScreen ? videoRef.current : null;
    if (!a) return;
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      const aOk = a.readyState >= 3;
      const vOk = !hasScreen || (v && v.readyState >= 3);
      if (aOk && vOk) setReady(true);
    };
    const onA = () => check();
    const onV = () => check();
    a.addEventListener('canplay', onA);
    a.addEventListener('canplaythrough', onA);
    a.addEventListener('loadeddata', onA);
    if (v) {
      v.addEventListener('canplay', onV);
      v.addEventListener('canplaythrough', onV);
      v.addEventListener('loadeddata', onV);
    }
    check();
    return () => {
      cancelled = true;
      a.removeEventListener('canplay', onA);
      a.removeEventListener('canplaythrough', onA);
      a.removeEventListener('loadeddata', onA);
      if (v) {
        v.removeEventListener('canplay', onV);
        v.removeEventListener('canplaythrough', onV);
        v.removeEventListener('loadeddata', onV);
      }
    };
  }, [hasScreen, recording?.id, screenMeta?.id]);

  // Sync voice -> screen on every voice 'timeupdate'. Screen is offset by
  // +offsetSec when it started AFTER voice.
  useEffect(() => {
    if (!hasScreen) return;
    const a = audioRef.current;
    const v = videoRef.current;
    if (!a || !v) return;
    const onTimeUpdate = () => {
      const target = Math.max(0, a.currentTime - offsetSec);
      // Only resync if drift > 200ms (avoids jitter on every frame).
      if (Math.abs(v.currentTime - target) > 0.2) {
        v.currentTime = target;
      }
    };
    const onPlay = () => {
      // Align + start both together (we've already waited on readyState).
      const target = Math.max(0, a.currentTime - offsetSec);
      if (Math.abs(v.currentTime - target) > 0.05) {
        v.currentTime = target;
      }
      if (v.paused) v.play().catch(() => {});
    };
    const onPause = () => { if (!v.paused) v.pause(); };
    const onSeeked = () => onTimeUpdate();
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    a.addEventListener('timeupdate', onTimeUpdate);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('seeked', onSeeked);
    a.addEventListener('waiting', onWaiting);
    a.addEventListener('playing', onPlaying);
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('playing', onPlaying);
    return () => {
      a.removeEventListener('timeupdate', onTimeUpdate);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('seeked', onSeeked);
      a.removeEventListener('waiting', onWaiting);
      a.removeEventListener('playing', onPlaying);
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('playing', onPlaying);
    };
  }, [hasScreen, offsetSec]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
        <div>
          <div className="text-xs text-surface-muted mb-1 flex items-center justify-between">
            <span>🔊 Voice</span>
            {!ready && hasScreen && (
              <span className="text-amber-500">⚪ Preparing…</span>
            )}
          </div>
          <audio
            ref={audioRef}
            src={recordingsApi.streamUrl(recording.id)}
            // 2026-07-15: controls disabled until both tracks are pre-
            // buffered so audio can't race the remote-fetched screen.
            // autoPlay removed for the same reason — QA reviewer clicks
            // play when both are ready.
            controls={ready}
            preload="auto"
            className="w-full"
          >
            Your browser does not support audio playback.
          </audio>
          {!ready && (
            <div className="mt-1 h-9 rounded-thin bg-surface-subtle dark:bg-surface-dark-subtle border border-surface-border dark:border-surface-dark-border flex items-center justify-center text-xs text-surface-muted">
              <span className="inline-block w-3 h-3 mr-2 border-2 border-surface-muted border-t-transparent rounded-full animate-spin" />
              Preparing playback…
            </div>
          )}
        </div>

        {hasScreen ? (
          <div className="relative">
            <div className="text-xs text-surface-muted mb-1">
              🎥 Screen
              <span className="ml-2 opacity-70">
                offset {Math.round((screenMeta.sync_offset_ms ?? 0))}ms
              </span>
            </div>
            <video
              ref={videoRef}
              src={screenRecordingsApi.streamUrl(screenMeta.id)}
              controls={false}
              muted
              playsInline
              preload="auto"
              className="w-full rounded-thin border border-surface-border dark:border-surface-dark-border bg-black"
            />
            {(!ready || buffering) && (
              <div className="absolute inset-0 mt-6 flex items-center justify-center bg-black/60 rounded-thin pointer-events-none">
                <div className="flex items-center gap-2 text-neutral-200 text-xs">
                  <span className="inline-block w-3 h-3 border-2 border-neutral-300 border-t-transparent rounded-full animate-spin" />
                  {ready ? 'Buffering…' : 'Preparing playback…'}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-surface-muted self-center">
            {!lookupDone
              ? 'Checking for linked screen recording…'
              : lookupError
                ? `Screen lookup error: ${String(lookupError?.message || lookupError)}`
                : '🎥 No screen recording for this call.'}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------
// EvaluatedCell + EvaluateAction (2026-07-15)
// ---------------------------------------------------------------------
// Shows the evaluated-status badge on each recording row, and provides
// the Evaluate / Edit-my-eval / Delete (QA Manager) actions.

function EvaluatedCell({ r }) {
  const ev = r.evaluation;
  if (!ev) {
    return (
      <td className="px-3 py-2 text-xs text-surface-muted">
        <span className="italic">Not evaluated</span>
      </td>
    );
  }
  const coached = !!ev.coached_at;
  return (
    <td className="px-3 py-2 text-xs">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1">
          <span className="text-emerald-600 dark:text-emerald-400" title={`Status: ${ev.status}`}>✅ Evaluated</span>
          {coached && (
            <span className="text-blue-600 dark:text-blue-300" title={`Coached${ev.coached_by_name ? ' by ' + ev.coached_by_name : ''}`}>
              · 🎓 Coached
            </span>
          )}
        </div>
        <div className="text-surface-muted truncate max-w-[220px]" title={ev.evaluator_name || ''}>
          by {ev.evaluator_name || '—'}
        </div>
        {ev.overall_score !== null && ev.overall_score !== undefined && (
          <div className="text-surface-muted">
            Score: {Number(ev.overall_score).toFixed(1)}
            {ev.passed === true  && <span className="ml-1 text-emerald-600">✓</span>}
            {ev.passed === false && <span className="ml-1 text-rose-600">✗</span>}
          </div>
        )}
      </div>
    </td>
  );
}

function EvaluateAction({ r }) {
  const { user } = useAuth() || {};
  const role = user?.role;
  const canWrite  = QA_EVAL_WRITER_ROLES.has(role);
  const isManager = QA_MANAGER_ROLES.has(role);
  const ev = r.evaluation;
  // 2026-07-16: View-evaluation modal state. Available to ALL roles when an
  // evaluation exists (backend enforces agents can only see evals of their
  // OWN recordings, and never drafts).
  const [viewOpen, setViewOpen] = useState(false);
  const openPlayback = (path) => {
    const url = `${window.location.origin}/playback/${r.id}${path || ''}`;
    window.open(url, `cx-playback-${r.id}`, 'width=1400,height=900,menubar=no,toolbar=no,location=no,status=no');
  };
  const del = async () => {
    if (!ev) return;
    if (!confirm(`Delete evaluation by ${ev.evaluator_name || 'unknown'}? This frees the recording for a fresh evaluation and cannot be undone.`)) return;
    try {
      await qaApi.deleteEvaluation(ev.evaluation_id);
      // Force a full page reload — quick + reliable, avoids re-plumbing
      // the react-query cache from here. Helen won't do this dozens of
      // times per minute so the reload cost is fine.
      window.location.reload();
    } catch (err) {
      alert(`Delete failed: ${err?.detail || err?.message || err}`);
    }
  };

  if (!canWrite && !ev) return null;

  // No evaluation yet: "Evaluate" call-to-action (writers only).
  if (!ev) {
    return (
      <button
        onClick={() => openPlayback('?evaluate=1')}
        className="text-emerald-600 dark:text-emerald-400 text-sm"
        title="Open the popout playback + evaluation form"
      >
        🎯 Evaluate
      </button>
    );
  }
  // Existing evaluation:
  //   - 👁 View eval  — ALL roles (2026-07-16). Opens the read-only modal.
  //   - ✏️ Edit eval  — writer roles only. Opens the popout with the form.
  //   - 🗑        — QA Manager only. Delete evaluation.
  const recLabel = [
    r.agent_extension && r.agent_name ? `${r.agent_extension} · ${r.agent_name}` : r.agent_extension || r.agent_name,
    r.direction,
    r.started_at ? new Date(r.started_at).toLocaleString() : null,
  ].filter(Boolean).join(' · ');
  return (
    <>
      <button
        onClick={() => setViewOpen(true)}
        className="text-continuum-600 dark:text-continuum-200 text-sm whitespace-nowrap"
        title="View the submitted evaluation (read-only)"
      >
        👁 View eval
      </button>
      {canWrite && (
        <button
          onClick={() => openPlayback('?evaluate=1')}
          className="text-continuum-600 dark:text-continuum-200 text-sm whitespace-nowrap"
          title="View or edit this evaluation"
        >
          ✏️ Edit eval
        </button>
      )}
      {isManager && (
        <button
          onClick={del}
          className="text-rose-600 dark:text-rose-400 text-sm whitespace-nowrap"
          title="Delete this evaluation (frees recording for re-evaluation)"
        >
          🗑
        </button>
      )}
      <EvaluationViewerModal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        recordingId={r.id}
        recording={r}
        recordingLabel={recLabel}
      />
    </>
  );
}
